/* eslint-disable @typescript-eslint/consistent-type-definitions */
import { useEffect, useReducer } from "react";
import clsx from "clsx";

import { Column } from "@/components";
import { CompaniesFilters } from "./CompaniesFilters";
import { CompaniesTable } from "./CompaniesTable";

import { CompanySummary } from "@/types";

type FilterState = {
  name: string;
};

type State = {
  allCompanies: CompanySummary[];
  filteredCompanies: CompanySummary[];
  filterState: FilterState;
};

type Action =
  | { type: "companies"; payload: CompanySummary[] }
  | { type: "name"; payload: string };

const filterCompanies = ({
  companies,
  filterState,
}: {
  companies: CompanySummary[];
  filterState: FilterState;
}) => {
  let filteredCompanies = [...companies];

  // 1. Filter by name.
  filteredCompanies = filteredCompanies.filter(({ name }) =>
    name.toLowerCase().includes(filterState.name.toLowerCase()),
  );

  return filteredCompanies;
};

const reducer = (state: State, action: Action): State => {
  // If new companies came in, then re-filter based on the new companies.
  if (action.type === "companies") {
    return {
      ...state,
      allCompanies: action.payload,
      filteredCompanies: filterCompanies({
        companies: action.payload,
        filterState: state.filterState,
      }),
    };
  }

  // Otherwise, we are updating the filter state.
  let newFilterState: FilterState = {
    ...state.filterState,
  };
  switch (action.type) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    case "name":
      newFilterState = {
        ...state.filterState,
        name: action.payload,
      };
      break;
    default:
      break;
  }

  // Set the new filter and filter the companies based on it.
  return {
    ...state,
    filteredCompanies: filterCompanies({
      companies: state.allCompanies,
      filterState: newFilterState,
    }),
    filterState: newFilterState,
  };
};

export const CompaniesFiltersAndTable = ({
  className,
  companies,
}: {
  className?: string;
  companies: CompanySummary[];
}) => {
  const [state, dispatch] = useReducer(reducer, {
    allCompanies: companies,
    filteredCompanies: companies,
    filterState: {
      name: "",
    },
  });

  useEffect(() => {
    // If a new company comes in (as the result of a creation), then update the state.
    dispatch({ type: "companies", payload: companies });
  }, [companies]);

  const setName = (name: string) => {
    dispatch({ type: "name", payload: name });
  };

  return (
    <Column className={clsx("gap-4", className)}>
      <CompaniesFilters name={state.filterState.name} setName={setName} />
      <CompaniesTable companies={state.filteredCompanies} />
    </Column>
  );
};
